import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { IProjectsService } from "../services/NewformaApi/IProjectsService";
import { ProjectFileItem } from "../models/projects/ProjectFilesResponse";
import { FileWithId } from "../models/shared/FileWithId";
import { Log } from "../services/Logger";
import { buildFolderNrn, parseFolderNrn, parseProjectNrn, isFolderNrn } from "../helpers/NrnHelpers";
import { FOLDER_FETCH_PAGE_MAX_SIZE } from "../constants/projectFiles";
import { MAX_ATTACHMENTS } from "../components/shared/attachments/attachmentList/AttachmentListHelpers";
import { hasFileExtension } from "../components/shared/attachments/AttachmentsHelpers";

/**
 * Determines if a ProjectFileItem is a folder using the most reliable signals
 * available, in priority order:
 *  1. NRN contains ":folder:" — the server-side type embedded in the NRN itself.
 *  2. `type` field === "folder".
 *  3. Has a `children` or `items` array (even if empty) — structure implies folder.
 */
function isFolderItem(item: ProjectFileItem): boolean {
    if (item.nrn && isFolderNrn(item.nrn)) return true;
    if (item.type === "folder") return true;
    if (Array.isArray(item.items) || Array.isArray(item.children)) return true;
    return false;
}

/** Joins a parent folder key and a child name into a full-path key (un-sliced, from project root). */
function joinKey(parentKey: string, name: string): string {
    return parentKey ? `${parentKey}\\${name}` : name;
}

/** Splits the API's forward-slash `folderPath` ("" | "b" | "email/2026-06") into segments. */
function splitFolderPath(folderPath: string | undefined): string[] {
    return (folderPath ?? "").split("/").filter(Boolean);
}

/** Where a selected file lands in the attachment list (mirrors `getAttachmentDisplayPath`). */
function getLandingPath(file: FileWithId): string {
    return file.addToParentFolder === false
        ? `${file.projectFolderRelativePath ?? ""}\\${file.name}`
        : file.displayPath ?? file.name;
}

/**
 * Name of the top-level node a selected file occupies in the attachment list — its
 * root folder name when folder-grouped, or its own name when it renders flat.
 * Mirrors `getAttachmentDisplayPath` in AttachmentListHelpers: two selections with
 * the same root name collapse into one node, so they must be treated as duplicates.
 */
function getAttachmentRootName(file: FileWithId): string {
    return (
        getLandingPath(file)
            .split("\\")
            .filter(Boolean)[0] ?? file.name
    );
}

/** Root segment of a previous-session key ("FolderA\\file.txt" → "FolderA"); undefined for NRN/bare-name keys. */
function getExistingKeyRoot(key: string): string | undefined {
    const slash = key.indexOf("\\");
    return slash > 0 ? key.substring(0, slash) : undefined;
}

/**
 * Duplicate detection is case-insensitive (`Report.pdf` and `report.pdf` land at the same
 * path — see the golden rule in doc/project-attachment-selection-rules.md). Every key stored
 * in or looked up against the existing-attachment set, and every same-session name/root
 * comparison, goes through this so both sides always agree on case.
 */
function normalizeKey(key: string): string {
    return key.toLowerCase();
}

interface FolderFlattenResult {
    files: FileWithId[];
    /** Folder full-path key → ids of every file beneath it (recursive), for the checked folder and each subfolder seen. */
    folderKeyToFileIds: Map<string, string[]>;
}

/**
 * Maps the flat `includeAllFolderContent=true` response (files only, each with a
 * `folderPath` relative to the checked folder) into FileWithId entries.
 *
 * @param pathBase     apiPath base for the checked folder (root project folder sliced off).
 * @param folderKey    Un-sliced full-path selection key of the checked folder.
 * @param displayBase  displayPath base — the checked folder's name.
 */
function flattenFolderFiles(
    items: ProjectFileItem[],
    pathBase: string,
    folderKey: string,
    displayBase: string,
    projectId: string,
    folderId: string
): FolderFlattenResult {
    const files: FileWithId[] = [];
    const folderKeys = new Set<string>([folderKey]);
    for (const item of items ?? []) {
        if (isFolderItem(item)) continue;
        const relativeSegments = splitFolderPath(item.folderPath);
        const itemPathBase = [pathBase, ...relativeSegments].join("\\");
        const itemDisplayBase = [displayBase, ...relativeSegments].join("\\");
        // Register every ancestor subfolder so un-checking it later can find its files.
        relativeSegments.reduce((parentKey, segment) => {
            const key = joinKey(parentKey, segment);
            folderKeys.add(key);
            return key;
        }, folderKey);
        // Use apiPath as the stable unique id — NRNs are NOT unique across folders.
        const apiPath = `${itemPathBase}\\${item.name}`;
        files.push({
            name: item.name,
            id: apiPath,
            // Backslash is the Newforma server's canonical path separator for display paths
            // (e.g. "FolderA\SubFolder\file.txt"). This is a server-side convention, not
            // a filesystem path — do not treat as a cross-platform path separator issue.
            displayPath: `${itemDisplayBase}\\${item.name}`,
            apiPath,
            fullPath: joinKey(folderKey, [...relativeSegments, item.name].join("\\")),
            file: new File([], item.name) as File & { path?: string },
            lastModifiedDate: new Date(item.lastModified),
            isFromProjectFolder: true,
            projectId,
            projectFolderRelativePath: itemDisplayBase,
            folderId,
        });
    }

    const folderKeyToFileIds = new Map<string, string[]>();
    folderKeys.forEach((key) => {
        const prefix = `${key}\\`;
        folderKeyToFileIds.set(
            key,
            files.filter((f) => f.fullPath?.startsWith(prefix)).map((f) => f.id)
        );
    });
    return { files, folderKeyToFileIds };
}

/**
 * Collects the full-path keys of all items that were explicitly checked inside a given
 * parent folder. Used when unchecking a folder to cascade-deselect all descendants.
 *
 * Builds a parent→children index once (O(n)) then does a BFS traversal (O(n)),
 * giving O(n) total instead of the O(n²) recursive forEach approach.
 */
function collectDescendantKeys(parentKey: string, keyParent: Map<string, string>): string[] {
    const children = new Map<string, string[]>();
    keyParent.forEach((parent, key) => {
        let list = children.get(parent);
        if (!list) {
            list = [];
            children.set(parent, list);
        }
        list.push(key);
    });

    const result: string[] = [];
    const queue = children.get(parentKey) ?? [];
    for (let i = 0; i < queue.length; i++) {
        const key = queue[i];
        result.push(key);
        const childList = children.get(key);
        if (childList) queue.push(...childList);
    }
    return result;
}

/**
 * True when every item visible on the level is selected: each file by its `apiPath`, each
 * folder by its full-path key in `checkedKeys` (a half-checked folder is not). Mirrors the
 * auto-check test in `onGoBack`.
 */
function isLevelFullySelected(
    files: FileWithId[],
    pathSegments: string[],
    browseItems: ProjectFileItem[],
    levelKey: string,
    checkedKeys: ReadonlySet<string>
): boolean {
    if (browseItems.length === 0) return false;
    const selectedApiPaths = new Set(files.map((f) => f.apiPath));
    return browseItems.every((item) =>
        isFolderItem(item)
            ? checkedKeys.has(joinKey(levelKey, item.name))
            : selectedApiPaths.has([...pathSegments, item.name].join("\\"))
    );
}

/**
 * Roll-up: when every item on a level is selected, the level's folder is attached as a whole,
 * so each file lands at `<level folder>\<its path below the level>`. Ancestors above the level
 * are dropped (see "Where things land" in doc/project-attachment-selection-rules.md). At the
 * project-folder root there is no level folder, so files keep their own folder path.
 * Returns copies so callers can inspect the result before committing it.
 */
function computeProjectFolderRelativePaths(files: FileWithId[], pathSegments: string[]): FileWithId[] {
    const levelFolder = pathSegments.length > 0 ? [pathSegments[pathSegments.length - 1]] : [];
    return files.map((file) => {
        const relativeSegments = getRelativeSegments(file, pathSegments);
        if (!relativeSegments) return file;
        const folders = [...levelFolder, ...relativeSegments.slice(0, -1)];
        if (folders.length === 0) return file;
        return { ...file, projectFolderRelativePath: folders.join("\\"), addToParentFolder: false };
    });
}

/** Segments of `file.apiPath` below `pathSegments`, or undefined when the file is not under that folder. */
function getRelativeSegments(file: FileWithId, pathSegments: string[]): string[] | undefined {
    if (!file.apiPath) return undefined;
    const segments = file.apiPath.split("\\");
    if (segments.length <= pathSegments.length) return undefined;
    return pathSegments.every((segment, index) => segments[index] === segment)
        ? segments.slice(pathSegments.length)
        : undefined;
}

/**
 * Reverses a roll-up after something on the level is unchecked: each promoted file goes back to
 * the landing of the item that was actually checked — its nearest checked folder below the
 * level, or flat when the file itself was checked.
 */
function unrollProjectFolderRelativePaths(
    files: FileWithId[],
    pathSegments: string[],
    levelKey: string,
    checkedKeys: ReadonlySet<string>
): FileWithId[] {
    const levelPrefix = `${levelKey}\\`;
    return files.map((file) => {
        if (file.addToParentFolder !== false || !file.fullPath || !getRelativeSegments(file, pathSegments)) {
            return file;
        }
        const fullPath = file.fullPath;
        const checkedFolderKey = Array.from(checkedKeys)
            .filter((key) => key.startsWith(levelPrefix) && fullPath.startsWith(`${key}\\`))
            .sort((a, b) => a.length - b.length)[0];
        if (!checkedFolderKey) {
            return { ...file, addToParentFolder: true, projectFolderRelativePath: "" };
        }
        const folderName = checkedFolderKey.slice(checkedFolderKey.lastIndexOf("\\") + 1);
        const relativeSegments = fullPath.slice(checkedFolderKey.length + 1).split("\\");
        return {
            ...file,
            projectFolderRelativePath: [folderName, ...relativeSegments.slice(0, -1)].join("\\"),
            addToParentFolder: false,
        };
    });
}

/** True when `file.apiPath` sits below the folder described by `pathSegments`. */
function isUnderPath(file: FileWithId, pathSegments: string[]): boolean {
    if (!file.apiPath) return false;
    const segments = file.apiPath.split("\\");
    return segments.length > pathSegments.length && pathSegments.every((s, i) => segments[i] === s);
}
// ─── Types ─────────────────────────────────────────────────────────────────

/**
 * Typed error events emitted by the hook so callers can map them to
 * translated strings without coupling the hook to any i18n library.
 */
export type ProjectFolderErrorKind =
    | "fileLimit" // Adding a file/folder would exceed maxFiles
    | "duplicate" // A file with the same folder path is already selected
    | "blockedFileType" // A file has no extension (same rule as the local file picker)
    | "fetchError"; // Network/server error while loading folder contents

/** Extra detail attached to an error so the consumer can build a specific message. */
export interface ProjectFolderErrorContext {
    /** Set when the error was triggered by checking a folder. */
    folderName?: string;
}

export interface UseProjectFolderSelectionResult {
    /** Files/folders visible in the current directory */
    browseItems: ProjectFileItem[];
    isLoadingBrowse: boolean;
    browseError: string | null;
    /** Full-path keys of items whose async operation is in flight */
    loadingKeys: string[];
    /** Set of all explicitly checked full-path keys (files + folders). O(1) lookup. */
    checkedKeys: ReadonlySet<string>;
    /** True when the current browse level was bulk-selected (folder checkbox or Select All),
     *  so all items should render as checked even if not individually in checkedKeys. */
    currentFolderIsSelected: boolean;
    /** Flat list of FileWithId ready for submission */
    selectedFiles: FileWithId[];
    /** Navigation */
    canGoBack: boolean;
    currentPath: string;
    /** The apiPath prefix for files at the current navigation level (root folder stripped). */
    currentApiPrefix: string;
    /** Full-path key of the current folder ("" at project root). Item key = currentFolderKey + "\\" + name. */
    currentFolderKey: string;
    onFolderClick: (item: ProjectFileItem) => void;
    onGoBack: () => void;
    /** Selection */
    onFileChecked: (item: ProjectFileItem, checked: boolean) => void;
    onFolderChecked: (item: ProjectFileItem, checked: boolean) => void;
    onRemoveFiles: (ids: string[]) => void;
    onClearAll: () => void;
    /** The most recent error kind, or null if no error / error was cleared. */
    lastError: ProjectFolderErrorKind | null;
    /** Detail for the most recent error (e.g. the folder that was rejected); null when none. */
    lastErrorContext: ProjectFolderErrorContext | null;
    /** Increments each time lastError is set, even if the kind is the same.
     *  Use as the useEffect dep so the consumer fires on every new error. */
    lastErrorSeq: number;
    /** Explicitly clear the current error (e.g. when the error banner is dismissed). */
    clearError: () => void;
    /** True when the current folder has more items available via pagination */
    hasMoreItems: boolean;
    /** True when loading the next page (as opposed to loading a new folder) */
    isLoadingMore: boolean;
    /** True when at least one additional page has been loaded beyond the first */
    hasPaginated: boolean;
    /** Fetches the next page of items for the current folder and appends them to browseItems */
    loadNextPage: () => void;
    /** Retries fetching the current folder (useful after an initial load error) */
    retryFetch: () => void;
    /** Reset everything (e.g. on project change) */
    reset: () => void;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

/**
 * @param existingFileIds  Mixed-format lookup set used to prevent re-adding already-attached
 *   files from a previous dialog session. Each entry is one of three key shapes:
 *     - File NRN (e.g. "nrn:file:abc123")        — same-project flat-file duplicate
 *     - Bare filename (e.g. "report.pdf")          — cross-project same-name duplicate
 *     - displayPath (e.g. "FolderA\\report.pdf")  — folder-fetched file duplicate
 *   Populated by ProjectFilesSection. Do NOT pass only NRNs — that silently breaks
 *   cross-project name detection and folder-path deduplication.
 *   Keys may be passed in any case; the hook lower-cases them and every lookup so that
 *   matching is case-insensitive (rules D7/D8).
 */
export function useProjectFolderSelection(
    projectNrn: string | undefined,
    projectsService: IProjectsService,
    maxFiles: number = MAX_ATTACHMENTS,
    resetKey?: number,
    existingFileIds?: ReadonlySet<string>,
    sortBy?: string
): UseProjectFolderSelectionResult {
    const [browseItems, setBrowseItems] = useState<ProjectFileItem[]>([]);
    const [isLoadingBrowse, setIsLoadingBrowse] = useState(false);
    const [browseError, setBrowseError] = useState<string | null>(null);
    const [loadingKeys, setLoadingKeys] = useState<string[]>([]);

    const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
    const [selectedFiles, setSelectedFiles] = useState<FileWithId[]>([]);
    const [lastError, setLastError] = useState<ProjectFolderErrorKind | null>(null);
    const [lastErrorContext, setLastErrorContext] = useState<ProjectFolderErrorContext | null>(null);
    const [lastErrorSeq, setLastErrorSeq] = useState(0);

    /** Always call this instead of setLastError directly so seq is always bumped. */
    const emitError = useCallback((kind: ProjectFolderErrorKind, context: ProjectFolderErrorContext | null = null) => {
        setLastError(kind);
        setLastErrorContext(context);
        setLastErrorSeq((s) => s + 1);
    }, []);

    /** Clears the current error — call when the error banner is dismissed. */
    const clearError = useCallback(() => {
        setLastError(null);
        setLastErrorContext(null);
    }, []);

    // Navigation stack. itemKey is the full-path key of the folder clicked to enter the current level.
    // currentFolderIsSelected is derived as: checkedKeys.has(navStack.last.itemKey).
    const [navStack, setNavStack] = useState<Array<{ nrn: string; path: string; itemKey: string }>>([]);
    const [currentNrn, setCurrentNrn] = useState<string | undefined>(undefined);
    const [currentPath, setCurrentPath] = useState("/");
    const currentFolderKey = navStack.length > 0 ? navStack[navStack.length - 1].itemKey : "";
    // Kept in sync on every render so onGoBack always reads the latest stack.
    const navStackRef = useRef(navStack);
    navStackRef.current = navStack;
    const currentFolderKeyRef = useRef(currentFolderKey);
    currentFolderKeyRef.current = currentFolderKey;
    const currentNrnRef = useRef(currentNrn);
    currentNrnRef.current = currentNrn;
    const currentPathRef = useRef(currentPath);
    currentPathRef.current = currentPath;
    // Kept in sync on every render so callbacks always read current values without closing over state.
    const selectedFilesRef = useRef<FileWithId[]>([]);
    selectedFilesRef.current = selectedFiles;
    const browseItemsRef = useRef<ProjectFileItem[]>([]);
    browseItemsRef.current = browseItems;
    // Shadow ref for existingFileIds so onFolderChecked (useCallback) always reads
    // the latest set without needing it as a dep.
    // Keys are mixed-format (see existingFileIds param JSDoc): NRN, bare name, or displayPath.
    // Stored lower-cased; every lookup must go through normalizeKey.
    const normalizedExistingFileIds = useMemo<ReadonlySet<string>>(
        () => new Set(Array.from(existingFileIds ?? []).map(normalizeKey)),
        [existingFileIds]
    );
    const existingFileIdsRef = useRef<ReadonlySet<string>>(normalizedExistingFileIds);
    existingFileIdsRef.current = normalizedExistingFileIds;
    const checkedKeysRef = useRef<Set<string>>(new Set());
    checkedKeysRef.current = checkedKeys;

    const isMounted = useRef(true);
    /** Track previous projectNrn/resetKey so the fetch effect can distinguish
     *  a project-switch (always fetch root) from a sort-only change (refetch current folder). */
    const prevProjectNrnRef = useRef(projectNrn);
    const prevResetKeyRef = useRef(resetKey);
    /** Maps each checked item's full-path key to the FileWithId ids it produced, for accurate reverse-lookup on removal. */
    const keyToFileIds = useRef<Map<string, string[]>>(new Map());
    /** Maps each checked full-path key to its parent folder's key ("" for root-level items).
     *  Used to cascade-deselect descendants when a folder is unchecked. */
    const keyParentRef = useRef<Map<string, string>>(new Map());
    /**
     * Maps each folder full-path key (at any depth) to the ids of all files contained
     * within it (recursively).  Populated whenever a folder is fetched so that
     * deselection can find the exact file IDs to remove even when the folder was
     * selected via a parent (and thus has no direct keyToFileIds entry).
     */
    const folderKeyToFileIdsRef = useRef<Map<string, string[]>>(new Map());
    /**
     * Maps each folder full-path key whose selection fetch is in flight to the attachment-list
     * root name it will produce (its `item.name`). Root-collision guards consult this so that
     * checking two same-named folders back to back is rejected even though the first folder's
     * files have not landed in selectedFiles yet.
     */
    const pendingFolderRootsRef = useRef<Map<string, string>>(new Map());
    useEffect(
        () => () => {
            isMounted.current = false;
        },
        []
    );

    // ── Browse helpers ──────────────────────────────────────────────────────

    const [browseOffsetToken, setBrowseOffsetToken] = useState<string | undefined>(undefined);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasPaginated, setHasPaginated] = useState(false);

    const fetchFolder = useCallback(
        async (nrn: string) => {
            setIsLoadingBrowse(true);
            setIsLoadingMore(false);
            setBrowseError(null);
            setBrowseOffsetToken(undefined);
            setHasPaginated(false);
            try {
                const response = await projectsService.getProjectFiles(
                    nrn,
                    FOLDER_FETCH_PAGE_MAX_SIZE,
                    false,
                    true,
                    sortBy,
                    false
                );
                if (isMounted.current) {
                    setBrowseItems(response?.items ?? []);
                    setBrowseOffsetToken(response?.paging?.offsetToken);
                }
            } catch (err) {
                Log.error("useProjectFolderSelection: failed to load folder", err);
                if (isMounted.current) {
                    setBrowseError("load_error");
                    setBrowseItems([]);
                    setBrowseOffsetToken(undefined);
                }
            } finally {
                if (isMounted.current) {
                    setIsLoadingBrowse(false);
                }
            }
        },
        [projectsService, sortBy]
    );

    // Reset all selection and navigation state whenever the project changes (including to undefined)
    // or resetKey increments. Runs unconditionally so deselecting a project clears selectedFiles
    // immediately and triggers onSelectionChange([]) via the effect that watches selectedFiles.
    useEffect(() => {
        keyToFileIds.current.clear();
        keyParentRef.current.clear();
        folderKeyToFileIdsRef.current.clear();
        pendingFolderRootsRef.current.clear();
        setNavStack([]);
        setCurrentNrn(projectNrn);
        setCurrentPath("/");
        setCheckedKeys(new Set());
        setSelectedFiles([]);
        setLastError(null);
        setLastErrorContext(null);
        setLastErrorSeq(0);
    }, [projectNrn, resetKey]);

    // Fetch folder contents only when a valid project NRN is available.
    // Kept separate from the reset effect so clearing the project never calls fetchFolder(undefined).
    useEffect(() => {
        if (!projectNrn) return;
        // On a project switch or reset, always fetch the new project root.
        // Only use the current subfolder NRN when just sortBy changed.
        const isProjectOrResetChange = prevProjectNrnRef.current !== projectNrn || prevResetKeyRef.current !== resetKey;
        prevProjectNrnRef.current = projectNrn;
        prevResetKeyRef.current = resetKey;
        const nrnToFetch = isProjectOrResetChange ? projectNrn : currentNrnRef.current ?? projectNrn;
        fetchFolder(nrnToFetch);
    }, [projectNrn, resetKey, sortBy]); // fetchFolder intentionally omitted — project/resetKey/sortBy change is the trigger

    /**
     * Builds the full NRN for a folder item and fetches its files in one recursive
     * request (`includeAllFolderContent=true` returns files only, each with a
     * `folderPath` relative to the folder). `maxSize` is one over the remaining
     * allowance so an over-limit folder is detected without pulling its whole tree:
     * `exceeded` is true when more than maxFiles files came back or the response
     * was truncated (paging.offsetToken present).
     */
    const fetchAndFlattenFolder = useCallback(
        async (item: ProjectFileItem): Promise<{ ids: string[]; files: FileWithId[]; exceeded: boolean }> => {
            const fullNrn = buildFolderNrn(item.nrn, projectNrn ?? "", currentNrn);
            const folderId = parseFolderNrn(fullNrn).projectFolderId;
            let projectId: string | undefined;
            try {
                projectId = projectNrn ? parseProjectNrn(projectNrn).projectId : undefined;
            } catch {
                projectId = undefined;
            }
            const response = await projectsService.getProjectFiles(fullNrn, maxFiles + 1, true, true);
            const items = response?.items ?? [];
            const exceeded = items.length > maxFiles || !!response?.paging?.offsetToken;
            // Build the apiPath pathBase by including the current navigation context
            // + the checked folder name.
            // e.g. if user is in "transfer/" and checks "drafts",
            // pathBase = "transfer\drafts" so files get apiPath "transfer\drafts\file.txt".
            // .slice(1) removes the root project folder (e.g. "My Newforma Projects").
            // Undo this slice if we need root level project for multiple project folder support.
            const ancestorSegments = [...navStackRef.current.map((e) => e.path), currentPathRef.current]
                .filter((s) => s && s !== "/")
                .slice(1);
            const pathBase = [...ancestorSegments, item.name].join("\\");
            const folderKey = joinKey(currentFolderKeyRef.current, item.name);
            const { files, folderKeyToFileIds } = flattenFolderFiles(
                items,
                pathBase,
                folderKey,
                item.name,
                projectId ?? "",
                folderId
            );
            if (!exceeded) {
                folderKeyToFileIds.forEach((fileIds, key) => {
                    folderKeyToFileIdsRef.current.set(key, fileIds);
                });
            }
            return { ids: files.map((f) => f.id), files, exceeded };
        },
        [projectNrn, currentNrn, projectsService, maxFiles]
    );

    const onFolderClick = useCallback(
        (item: ProjectFileItem) => {
            if (!projectNrn || !currentNrn) return;
            const fullNrn = buildFolderNrn(item.nrn, projectNrn, currentNrn);
            const stack = navStackRef.current;
            const itemKey = joinKey(currentFolderKeyRef.current, item.name);

            // Selected folders are NOT browsable — the user must uncheck the folder
            // first to navigate into it.  This also covers the (unreachable in the UI)
            // case where the whole current level is selected via a parent.
            const currentLevelIsSelected =
                stack.length > 0 && checkedKeysRef.current.has(stack[stack.length - 1].itemKey);
            const itemIsChecked = checkedKeysRef.current.has(itemKey);
            if (itemIsChecked || currentLevelIsSelected) {
                return;
            }

            setNavStack((prev) => [...prev, { nrn: currentNrn, path: currentPath, itemKey }]);
            setCurrentNrn(fullNrn);
            setCurrentPath(item.name);
            fetchFolder(fullNrn);
        },
        [projectNrn, currentNrn, currentPath, fetchFolder]
    );

    const onGoBack = useCallback(() => {
        const stack = navStackRef.current;
        if (stack.length === 0) return;
        const parent = stack[stack.length - 1];
        const currentKey = parent.itemKey;
        const isRootLevelFolder = stack.length === 1;

        // With navigation into selected folders blocked, a folder can never be
        // implicitly selected while we are inside it.  The only reason to auto-check
        // it on the way out is if every visible child was checked by the user.
        // Files are verified via apiPath in selectedFiles; folders via their full-path key.
        const pathSegments = [...navStackRef.current.map((e) => e.path), currentPathRef.current]
            .filter((s) => s && s !== "/")
            .slice(1);
        const allChildrenChecked = isLevelFullySelected(
            selectedFilesRef.current,
            pathSegments,
            browseItemsRef.current,
            currentKey,
            checkedKeysRef.current
        );

        setNavStack(stack.slice(0, -1));
        setCurrentNrn(parent.nrn);
        setCurrentPath(parent.path);

        setCheckedKeys((prev) => {
            const next = new Set(prev);
            if (allChildrenChecked && !isRootLevelFolder) {
                next.add(currentKey);
            } else {
                next.delete(currentKey);
            }
            return next;
        });
        if (allChildrenChecked && !isRootLevelFolder) {
            const grandParentKey = stack.length > 1 ? stack[stack.length - 2].itemKey : "";
            keyParentRef.current.set(currentKey, grandParentKey);
        } else {
            keyParentRef.current.delete(currentKey);
        }

        fetchFolder(parent.nrn);
    }, [fetchFolder]);

    /**
     * True when a roll-up of the level at `pathSegments` would put a file where something already
     * is: a previous-session attachment, a selection outside the level, or a folder still being
     * fetched (other than `ownFolderKey`, the folder whose check triggered the roll-up).
     * Compares landing paths and their top-level folder name, case-insensitively.
     */
    const rollUpCollides = useCallback((rolled: FileWithId[], pathSegments: string[], ownFolderKey?: string) => {
        const outsideRoots = new Set(
            selectedFilesRef.current
                .filter((f) => !isUnderPath(f, pathSegments))
                .map((f) => normalizeKey(getAttachmentRootName(f)))
        );
        const pendingRoots = new Set<string>();
        pendingFolderRootsRef.current.forEach((rootName, key) => {
            if (key !== ownFolderKey) pendingRoots.add(normalizeKey(rootName));
        });
        const existingRoots = new Set<string>();
        existingFileIdsRef.current.forEach((key) => {
            const root = getExistingKeyRoot(key);
            if (root) existingRoots.add(root);
        });
        return rolled.some((f) => {
            if (!isUnderPath(f, pathSegments)) return false;
            const landing = normalizeKey(getLandingPath(f));
            const root = normalizeKey(getAttachmentRootName(f));
            return (
                existingFileIdsRef.current.has(landing) ||
                existingRoots.has(root) ||
                existingFileIdsRef.current.has(root) ||
                outsideRoots.has(root) ||
                pendingRoots.has(root)
            );
        });
    }, []);

    const onFileChecked = useCallback(
        (item: ProjectFileItem, checked: boolean) => {
            let projectId: string | undefined;
            try {
                projectId = projectNrn ? parseProjectNrn(projectNrn).projectId : undefined;
            } catch {
                projectId = undefined;
            }

            if (checked) {
                if (selectedFilesRef.current.length >= maxFiles) {
                    emitError("fileLimit");
                    return;
                }

                // Files without an extension are blocked, matching the local file picker (rule V1).
                if (!hasFileExtension(item.name)) {
                    emitError("blockedFileType");
                    return;
                }

                // Build the relative path from navStack paths + currentPath + file name
                // e.g. "folderA\email.txt" for API use.
                const pathSegments = [...navStackRef.current.map((e) => e.path), currentPathRef.current]
                    .filter((s) => s && s !== "/")
                    .slice(1);
                const apiPath = [...pathSegments, item.name].join("\\");
                const parentKey = currentFolderKeyRef.current;
                const fileKey = joinKey(parentKey, item.name);

                // Guard: no-op when this exact node is already checked. Keyed on fullPath (which
                // keeps the project-folder segment) rather than apiPath (which strips it), so a
                // same-named root file in a different project folder falls through to the
                // duplicate checks below instead of being silently ignored.
                if (selectedFilesRef.current.some((f) => f.fullPath === fileKey)) return;

                // Reject if this file was already attached in a previous dialog session.
                // - NRN check: same file, same project (flat attachment)
                // - Name check: same filename as an existing flat attachment (individually-checked
                //   files have no displayPath and render at the same UI level, so same-named
                //   files would appear identical regardless of their source folder)
                // A file attached with its folder in a prior session lands at "folder\name", not
                // at its bare name, so it is NOT a duplicate of this flat pick (rule D1); the
                // clash only appears if the whole level rolls up — checked below.
                if (
                    existingFileIdsRef.current.has(normalizeKey(item.nrn)) ||
                    existingFileIdsRef.current.has(normalizeKey(item.name))
                ) {
                    emitError("duplicate");
                    return;
                }

                // Reject if a file with the same relative path (apiPath) is already selected
                // in the current session — added via a folder check, or the same root-relative
                // path under a different project folder (both land at the same attachment path).
                if (selectedFilesRef.current.some((f) => f.apiPath === apiPath)) {
                    emitError("duplicate");
                    return;
                }

                // Reject if a file with the same bare name is already selected and both
                // would render at the same level in the attachment list UI.
                // Individually-checked files have no displayPath and render as flat items,
                // so two "hi.txt" from different folders would appear identical to the user.
                const itemNameKey = normalizeKey(item.name);
                if (selectedFilesRef.current.some((f) => !f.displayPath && normalizeKey(f.name) === itemNameKey)) {
                    emitError("duplicate");
                    return;
                }

                // Reject if a folder already occupies this name at the top level of the
                // attachment list (this session, in flight, or a previous one) — a flat
                // file and a folder with the same name would collapse into the same node.
                const collidesWithSelectedRoot = selectedFilesRef.current.some(
                    (f) => normalizeKey(getAttachmentRootName(f)) === itemNameKey
                );
                const collidesWithPendingRoot = Array.from(pendingFolderRootsRef.current.values()).some(
                    (rootName) => normalizeKey(rootName) === itemNameKey
                );
                let collidesWithExistingRoot = false;
                existingFileIdsRef.current.forEach((key) => {
                    if (getExistingKeyRoot(key) === itemNameKey) collidesWithExistingRoot = true;
                });
                if (collidesWithSelectedRoot || collidesWithPendingRoot || collidesWithExistingRoot) {
                    emitError("duplicate");
                    return;
                }

                const fullNrn = buildFolderNrn(item.nrn, projectNrn ?? "", currentNrn);
                const folderId = parseFolderNrn(fullNrn).projectFolderId;

                const newFile: FileWithId = {
                    name: item.name,
                    id: item.nrn,
                    // Do NOT set displayPath on individually-checked files.
                    // The attachment list uses displayPath for folder-grouping display;
                    // absence means the file renders as a flat item using its name.
                    apiPath,
                    fullPath: fileKey,
                    file: new File([], item.name) as File & { path?: string },
                    lastModifiedDate: new Date(item.lastModified),
                    isFromProjectFolder: true,
                    projectId,
                    addToParentFolder: true,
                    projectFolderRelativePath: "",
                    folderId,
                };
                let nextSelectedFiles = [...selectedFilesRef.current, newFile];
                if (
                    isLevelFullySelected(
                        nextSelectedFiles,
                        pathSegments,
                        browseItemsRef.current,
                        parentKey,
                        checkedKeysRef.current
                    )
                ) {
                    // Checking the last item rolls the level up into its folder, which moves every
                    // file here to "folder\name". Reject the pick if any of those landing paths
                    // (or their top-level folder) is already taken.
                    const rolled = computeProjectFolderRelativePaths(nextSelectedFiles, pathSegments);
                    if (rollUpCollides(rolled, pathSegments)) {
                        emitError("duplicate");
                        return;
                    }
                    nextSelectedFiles = rolled;
                }

                keyParentRef.current.set(fileKey, parentKey);
                keyToFileIds.current.set(fileKey, [apiPath]);
                setCheckedKeys((prev) => {
                    const next = new Set(prev);
                    next.add(fileKey);
                    return next;
                });
                selectedFilesRef.current = nextSelectedFiles;
                setSelectedFiles(nextSelectedFiles);
            } else {
                // Single-branch explicit deselect — no implicit mode possible since
                // deselect-on-navigate-in guarantees selection is always explicit here.
                const levelKey = currentFolderKeyRef.current;
                const fileKey = joinKey(levelKey, item.name);
                // Build the same apiPath used when the file was added, so we
                // remove by path-based id (unique across folders) not by NRN.
                const pathSegments = [...navStackRef.current.map((e) => e.path), currentPathRef.current]
                    .filter((s) => s && s !== "/")
                    .slice(1);
                // Decide before any setter runs, while refs still describe the pre-uncheck state.
                const wasRolledUp =
                    pathSegments.length > 0 &&
                    isLevelFullySelected(
                        selectedFilesRef.current,
                        pathSegments,
                        browseItemsRef.current,
                        levelKey,
                        checkedKeysRef.current
                    );
                keyToFileIds.current.delete(fileKey);
                keyParentRef.current.delete(fileKey);

                // Also remove any ancestor folder keys that are no longer fully selected.
                const ancestorKeys = navStackRef.current.map((e) => e.itemKey);
                ancestorKeys.forEach((k) => {
                    keyParentRef.current.delete(k);
                    keyToFileIds.current.delete(k);
                });
                setCheckedKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(fileKey);
                    ancestorKeys.forEach((k) => next.delete(k));
                    return next;
                });

                const uncheckedApiPath = [...pathSegments, item.name].join("\\");
                setSelectedFiles((prev) => {
                    const remaining = prev.filter((f) => f.apiPath !== uncheckedApiPath);
                    return wasRolledUp
                        ? unrollProjectFolderRelativePaths(remaining, pathSegments, levelKey, checkedKeysRef.current)
                        : remaining;
                });
            }
        },
        [currentNrn, maxFiles, projectNrn, rollUpCollides]
    );

    const onFolderChecked = useCallback(
        async (item: ProjectFileItem, checked: boolean) => {
            const parentKey = currentFolderKeyRef.current;
            const folderKey = joinKey(parentKey, item.name);
            const pathSegments = [...navStackRef.current.map((e) => e.path), currentPathRef.current]
                .filter((s) => s && s !== "/")
                .slice(1);
            if (!checked) {
                // Decide before any setter runs, while refs still describe the pre-uncheck state.
                const wasRolledUp =
                    pathSegments.length > 0 &&
                    isLevelFullySelected(
                        selectedFilesRef.current,
                        pathSegments,
                        browseItemsRef.current,
                        parentKey,
                        checkedKeysRef.current
                    );
                // Capture the stored file ids BEFORE deleting, so idsToRemove is built correctly.
                const ownFileIds = keyToFileIds.current.get(folderKey) ?? [];
                keyToFileIds.current.delete(folderKey);

                // Cascade: collect all descendants that were explicitly checked inside this folder.
                const descendants = collectDescendantKeys(folderKey, keyParentRef.current);
                const ancestorKeys = navStackRef.current.map((e) => e.itemKey);

                // Clean up keyParentRef before any setters (Strict Mode safe).
                keyParentRef.current.delete(folderKey);
                descendants.forEach((d) => {
                    keyParentRef.current.delete(d);
                    keyToFileIds.current.delete(d);
                });

                // Single-branch explicit deselect — deselect-on-navigate-in guarantees
                // there is no implicit mode to transition from.
                ancestorKeys.forEach((k) => {
                    keyParentRef.current.delete(k);
                    keyToFileIds.current.delete(k);
                });
                setCheckedKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(folderKey);
                    descendants.forEach((d) => next.delete(d));
                    ancestorKeys.forEach((k) => next.delete(k));
                    return next;
                });
                // Build the complete set of file ids to remove:
                //   • Files directly fetched when this folder was checked (captured above)
                //   • Files discovered via a parent-folder fetch (folderKeyToFileIdsRef)
                //   • Files/folders from any explicitly-checked descendants
                const idsToRemove = new Set<string>([
                    ...ownFileIds,
                    ...(folderKeyToFileIdsRef.current.get(folderKey) ?? []),
                ]);
                descendants.forEach((d) => {
                    idsToRemove.add(d);
                    (keyToFileIds.current.get(d) ?? []).forEach((id) => idsToRemove.add(id));
                    (folderKeyToFileIdsRef.current.get(d) ?? []).forEach((id) => idsToRemove.add(id));
                });
                setSelectedFiles((prev) => {
                    const remaining = prev.filter((f) => !idsToRemove.has(f.id) && !idsToRemove.has(f.apiPath ?? ""));
                    return wasRolledUp
                        ? unrollProjectFolderRelativePaths(remaining, pathSegments, parentKey, checkedKeysRef.current)
                        : remaining;
                });
                return;
            }

            // Cross-project folder duplicate check: reject before fetching if a folder with
            // the same name was already attached (i.e. any existing displayPath starts with
            // "folderName\").  Since pathBase is always item.name, the first backslash-segment
            // of every displayPath in existingFileIds IS the folder root name.
            // A previously attached flat file with the same bare name also occupies that
            // top-level node, so it is rejected too.
            const folderNameKey = normalizeKey(item.name);
            const existingFolderRoots = new Set<string>();
            existingFileIdsRef.current.forEach((key) => {
                const root = getExistingKeyRoot(key);
                if (root) existingFolderRoots.add(root);
            });
            if (existingFolderRoots.has(folderNameKey) || existingFileIdsRef.current.has(folderNameKey)) {
                emitError("duplicate");
                return;
            }

            // Same-session root collision: displayPath is rooted at the checked folder's
            // *name*, not its full path, so "test1\foldertest" and "test2\foldertest" both
            // produce "foldertest\..." and would merge into one node in the attachment list.
            // Reject if any selected file outside this folder's own subtree already occupies
            // that root name (another same-named folder, or a flat file with that name).
            // Files inside this folder's subtree are excluded — they are about to be replaced
            // by the fresh fetch (re-check, or promoting individually checked descendants).
            const ownSubtreePrefix = `${folderKey}\\`;
            const collidesWithSelectedRoot = selectedFilesRef.current.some(
                (f) =>
                    !f.fullPath?.startsWith(ownSubtreePrefix) &&
                    normalizeKey(getAttachmentRootName(f)) === folderNameKey
            );
            // Also reject if another folder with this root name is still being fetched: its
            // files are not in selectedFiles yet, so the check above alone would let both
            // requests through and append under the same root.
            let collidesWithPendingRoot = false;
            pendingFolderRootsRef.current.forEach((rootName, pendingKey) => {
                if (pendingKey !== folderKey && normalizeKey(rootName) === folderNameKey)
                    collidesWithPendingRoot = true;
            });
            if (collidesWithSelectedRoot || collidesWithPendingRoot) {
                emitError("duplicate");
                return;
            }

            // Capture stale IDs BEFORE the fetch so that fetchAndFlattenFolder's side-effect
            // of writing into folderKeyToFileIdsRef does not pollute idsToClean.
            // idsToClean is meant to contain only ids from a PRIOR check of this same folder
            // (the clean-and-replace pattern).
            const staleOwnIds = [...(keyToFileIds.current.get(folderKey) ?? [])];
            const staleFolderIds = [...(folderKeyToFileIdsRef.current.get(folderKey) ?? [])];

            // Optimistic checkbox — record parent relationship before fetch.
            keyParentRef.current.set(folderKey, parentKey);
            setCheckedKeys((prev) => {
                const next = new Set(prev);
                next.add(folderKey);
                return next;
            });
            setLoadingKeys((prev) => [...prev, folderKey]);
            pendingFolderRootsRef.current.set(folderKey, item.name);

            try {
                const { ids, files, exceeded } = await fetchAndFlattenFolder(item);
                if (isMounted.current) {
                    // Clean-and-replace: remove any files/descendants previously tracked
                    // under this folder (handles stale sibling ids left by an earlier
                    // implicit→explicit transition), then add the full fresh fetch result.
                    const lingeringDescendants = collectDescendantKeys(folderKey, keyParentRef.current);
                    const idsToClean = new Set<string>([...staleOwnIds, ...staleFolderIds]);
                    lingeringDescendants.forEach((d) => {
                        (keyToFileIds.current.get(d) ?? []).forEach((id) => idsToClean.add(id));
                        (folderKeyToFileIdsRef.current.get(d) ?? []).forEach((id) => idsToClean.add(id));
                    });
                    // Select-all on a partially selected folder: any file already selected
                    // under this folder's path (individually checked or via a subfolder)
                    // is replaced by the fresh folder fetch so the whole folder becomes a
                    // single, consistently-grouped selection. Catches descendants whose
                    // parent chain in keyParentRef is broken (e.g. checked deep inside a
                    // never-checked subfolder).
                    const folderKeyPrefix = `${folderKey}\\`;
                    const flatDescendantKeys: string[] = [];
                    selectedFilesRef.current.forEach((f) => {
                        if (f.fullPath?.startsWith(folderKeyPrefix)) {
                            idsToClean.add(f.id);
                            if (f.apiPath) idsToClean.add(f.apiPath);
                            flatDescendantKeys.push(f.fullPath);
                        }
                    });
                    const keysToEvict = [...lingeringDescendants, ...flatDescendantKeys];
                    const baseCount = selectedFilesRef.current.filter(
                        (f) => !idsToClean.has(f.id) && !idsToClean.has(f.apiPath ?? "")
                    ).length;
                    if (exceeded || baseCount + files.length > maxFiles) {
                        keyParentRef.current.delete(folderKey);
                        setCheckedKeys((prev) => {
                            const next = new Set(prev);
                            next.delete(folderKey);
                            return next;
                        });
                        emitError("fileLimit", { folderName: item.name });
                    } else if (files.some((f) => !hasFileExtension(f.name))) {
                        // A folder containing an extension-less file is rejected as a whole,
                        // matching the local picker which blocks the entire drop (rule V1).
                        keyParentRef.current.delete(folderKey);
                        setCheckedKeys((prev) => {
                            const next = new Set(prev);
                            next.delete(folderKey);
                            return next;
                        });
                        emitError("blockedFileType", { folderName: item.name });
                    } else {
                        // Dedup: split into two categories:
                        //   1. Previous-session duplicates (existingFileIdsRef) — warn + uncheck folder.
                        //   2. Current-session duplicates (same NRN or displayPath already in
                        //      selectedFiles this session) — silently skip, keep folder checked.
                        //      This covers individually-checked files (no displayPath) that live
                        //      inside a folder the user is now checking at a higher level.
                        // currentSessionNrns: flat (non-folder) files only, keyed by NRN.
                        // Folder-fetched files with the same NRN but different displayPaths
                        // (e.g. filea/hi.txt and fileb/hi.txt) are distinct attachments and
                        // must NOT be deduplicated by NRN alone.
                        const isCleaned = (f: FileWithId) => idsToClean.has(f.id) || idsToClean.has(f.apiPath ?? "");
                        const currentSessionNrns = new Set<string>(
                            selectedFilesRef.current.filter((f) => !isCleaned(f) && !f.displayPath).map((f) => f.id)
                        );
                        const existingPaths = new Set<string>(
                            selectedFilesRef.current
                                .filter((f) => !isCleaned(f) && f.displayPath != null)
                                .map((f) => f.displayPath as string)
                        );
                        const existingApiPaths = new Set<string>(
                            selectedFilesRef.current
                                .filter((f) => !isCleaned(f) && f.apiPath != null)
                                .map((f) => f.apiPath as string)
                        );
                        const newFiles = files.filter((f) => {
                            // Key: folder-fetched files use displayPath; flat files use NRN.
                            // Matches the key built in ProjectFilesSection so a flat-attached
                            // file does NOT block the same file when added via a folder
                            // (different path context = different key = not a duplicate).
                            const attachmentKey = f.displayPath ?? f.id;
                            if (existingFileIdsRef.current.has(normalizeKey(attachmentKey))) return false;
                            // Block flat (individually-checked) files already in this session.
                            // Folder-fetched files are already covered by the displayPath check below.
                            if (!f.displayPath && currentSessionNrns.has(f.id)) return false;
                            if (f.displayPath && existingPaths.has(f.displayPath)) return false;
                            // Block files with the same relative path (apiPath) already selected.
                            if (f.apiPath && existingApiPaths.has(f.apiPath)) return false;
                            return true;
                        });
                        const hasPreviousSessionDuplicates = files.some((f) =>
                            existingFileIdsRef.current.has(normalizeKey(f.displayPath ?? f.id))
                        );
                        if (hasPreviousSessionDuplicates) {
                            // A file from a previous dialog session is already attached —
                            // do not add any files and uncheck the folder to signal it was
                            // not added.
                            keyToFileIds.current.delete(folderKey);
                            keyParentRef.current.delete(folderKey);
                            setCheckedKeys((prev) => {
                                const next = new Set(prev);
                                next.delete(folderKey);
                                return next;
                            });
                            emitError("duplicate");
                            return;
                        }

                        const cleaned = selectedFilesRef.current.filter((f) => !isCleaned(f));
                        let nextSelectedFiles = [...cleaned, ...newFiles];
                        // This folder was checked optimistically before the fetch; include it
                        // explicitly so the test does not depend on that state having rendered.
                        const checkedWithSelf = new Set(checkedKeysRef.current);
                        checkedWithSelf.add(folderKey);
                        if (
                            isLevelFullySelected(
                                nextSelectedFiles,
                                pathSegments,
                                browseItemsRef.current,
                                parentKey,
                                checkedWithSelf
                            )
                        ) {
                            // Same as the file case: checking the last item rolls the level up, so
                            // reject the folder if the rolled-up landing paths are already taken.
                            const rolled = computeProjectFolderRelativePaths(nextSelectedFiles, pathSegments);
                            if (rollUpCollides(rolled, pathSegments, folderKey)) {
                                keyToFileIds.current.delete(folderKey);
                                keyParentRef.current.delete(folderKey);
                                setCheckedKeys((prev) => {
                                    const next = new Set(prev);
                                    next.delete(folderKey);
                                    return next;
                                });
                                emitError("duplicate");
                                return;
                            }
                            nextSelectedFiles = rolled;
                        }

                        // Commit descendant ownership transfer only after all guard checks pass.
                        lingeringDescendants.forEach((d) => {
                            keyToFileIds.current.delete(d);
                            keyParentRef.current.delete(d);
                        });
                        flatDescendantKeys.forEach((d) => {
                            keyToFileIds.current.delete(d);
                            keyParentRef.current.delete(d);
                        });
                        if (keysToEvict.length > 0) {
                            setCheckedKeys((prev) => {
                                const next = new Set(prev);
                                keysToEvict.forEach((d) => next.delete(d));
                                return next;
                            });
                        }

                        keyToFileIds.current.set(
                            folderKey,
                            newFiles.map((f) => f.id)
                        );
                        selectedFilesRef.current = nextSelectedFiles;
                        setSelectedFiles(nextSelectedFiles);
                    }
                }
            } catch (err) {
                if (isMounted.current) {
                    keyToFileIds.current.delete(folderKey);
                    keyParentRef.current.delete(folderKey);
                    setCheckedKeys((prev) => {
                        const next = new Set(prev);
                        next.delete(folderKey);
                        return next;
                    });
                    Log.error("useProjectFolderSelection: failed to load folder for selection", err);
                    emitError("fetchError");
                }
            } finally {
                pendingFolderRootsRef.current.delete(folderKey);
                if (isMounted.current) {
                    setLoadingKeys((prev) => prev.filter((k) => k !== folderKey));
                }
            }
        },
        [fetchAndFlattenFolder, maxFiles, rollUpCollides]
    );

    const onRemoveFiles = useCallback(
        (ids: string[]) => {
            const idSet = new Set(ids);
            // Read via ref — always current without closing over selectedFiles state.
            // Match against both id and apiPath since individually-checked files have
            // id=NRN while folder-fetched files have id=apiPath.
            const remaining = selectedFilesRef.current.filter((f) => !idSet.has(f.id) && !idSet.has(f.apiPath ?? ""));
            // Build a set of apiPaths from remaining files for stale-NRN detection.
            // keyToFileIds stores apiPath values, so we check against apiPaths.
            const remainingApiPaths = new Set(remaining.map((f) => f.apiPath).filter(Boolean));

            const staleKeys = new Set<string>();
            Array.from(keyToFileIds.current.entries()).forEach(([key, produced]) => {
                if (!produced.some((id: string) => remainingApiPaths.has(id))) {
                    staleKeys.add(key);
                    keyToFileIds.current.delete(key);
                }
            });

            setSelectedFiles(remaining);
            if (staleKeys.size > 0) {
                staleKeys.forEach((key) => keyParentRef.current.delete(key));
                setCheckedKeys((prev) => {
                    const next = new Set(prev);
                    staleKeys.forEach((key) => next.delete(key));
                    return next;
                });
            }
        },
        [] // everything is read via refs; no external deps needed
    );

    const onClearAll = useCallback(() => {
        keyToFileIds.current.clear();
        keyParentRef.current.clear();
        folderKeyToFileIdsRef.current.clear();
        pendingFolderRootsRef.current.clear();
        setSelectedFiles([]);
        setCheckedKeys(new Set());
    }, []);

    const loadNextPage = useCallback(async () => {
        if (!browseOffsetToken || !currentNrn) return;
        setIsLoadingMore(true);
        setIsLoadingBrowse(true);
        setBrowseError(null);
        try {
            const response = await projectsService.getProjectFilesPage(
                currentNrn,
                FOLDER_FETCH_PAGE_MAX_SIZE,
                false,
                true,
                browseOffsetToken,
                sortBy
            );
            if (isMounted.current) {
                setBrowseItems((prev) => [...prev, ...(response?.items ?? [])]);
                setBrowseOffsetToken(response?.paging?.offsetToken);
                setHasPaginated(true);
            }
        } catch (err) {
            Log.error("useProjectFolderSelection: failed to load next page", err);
            if (isMounted.current) {
                setBrowseError("load_error");
            }
        } finally {
            if (isMounted.current) {
                setIsLoadingBrowse(false);
                setIsLoadingMore(false);
            }
        }
    }, [browseOffsetToken, currentNrn, projectsService, sortBy]);

    const reset = useCallback(() => {
        keyToFileIds.current.clear();
        keyParentRef.current.clear();
        folderKeyToFileIdsRef.current.clear();
        setBrowseItems([]);
        setBrowseError(null);
        setIsLoadingBrowse(false);
        setLoadingKeys([]);
        setCheckedKeys(new Set());
        setSelectedFiles([]);
        setNavStack([]);
        setCurrentNrn(undefined);
        setCurrentPath("/");
        setLastError(null);
        setLastErrorContext(null);
        setLastErrorSeq(0);
        setBrowseOffsetToken(undefined);
        setHasPaginated(false);
    }, []);

    const retryFetch = useCallback(() => {
        const nrn = currentNrn ?? projectNrn;
        if (nrn) fetchFolder(nrn);
    }, [currentNrn, projectNrn, fetchFolder]);

    // Derived — never stored state, so it cannot get out of sync with checkedKeys.
    const currentFolderIsSelected = navStack.length > 0 && checkedKeys.has(navStack[navStack.length - 1].itemKey);

    // Derived: the apiPath prefix for files at the current browse level.
    const currentApiPrefix = useMemo(() => {
        const segments = [...navStack.map((e) => e.path), currentPath].filter((s) => s && s !== "/").slice(1);
        return segments.join("\\");
    }, [navStack, currentPath]);

    return {
        browseItems,
        isLoadingBrowse,
        isLoadingMore,
        hasPaginated,
        browseError,
        loadingKeys,
        checkedKeys,
        currentFolderIsSelected,
        selectedFiles,
        lastError,
        lastErrorContext,
        lastErrorSeq,
        clearError,
        hasMoreItems: !!browseOffsetToken,
        loadNextPage,
        retryFetch,
        canGoBack: navStack.length > 0,
        currentPath,
        currentApiPrefix,
        currentFolderKey,
        onFolderClick,
        onGoBack,
        onFileChecked,
        onFolderChecked,
        onRemoveFiles,
        onClearAll,
        reset,
    };
}
